import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Divider, MenuItem, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";
import type { MeasureSubgroup } from "../../../../../../../../../api/types/svTypes.ts";
import { selectMeasureSubgroups } from "../../../../../store/selectors/metaDataSelectors";
import { getGroupSubgroups, resolveSubgroupId } from "../utilities/measureSubgroups";
import NewSubgroupDialog from "./NewSubgroupDialog";

interface Props {
  group: string | undefined;
  subgroupId: string | undefined;
  disabled?: boolean;
  onChange: (subgroupId: string | undefined) => void;
}

const SubgroupSelect = ({ group, subgroupId, disabled, onChange }: Props) => {
  const { hasAnyPermission } = useClientPermissionsContext();
  const measureSubgroups = useSelector(selectMeasureSubgroups);

  const [showNewSubgroup, setShowNewSubgroup] = useState(false);

  const groupSubgroups = getGroupSubgroups(measureSubgroups, group);
  const value = resolveSubgroupId(subgroupId, groupSubgroups) ?? "";
  const canCreateSubgroup = hasAnyPermission(["ManageCustomMeasures"]);

  const handleCreated = (subgroup: MeasureSubgroup) => onChange(subgroup.id);

  return (
    <>
      <TextField
        select
        fullWidth
        label="Sub-Group"
        value={value}
        disabled={disabled || !group}
        slotProps={{
          inputLabel: { shrink: true },
          select: { displayEmpty: true, MenuProps: { slotProps: { paper: { sx: { width: 0 } } } } },
        }}
        onChange={(event) => {
          if (event.target.value) {
            onChange(event.target.value);
          }
        }}
      >
        <MenuItem value="" onClick={() => onChange(undefined)}>
          <Typography noWrap>None</Typography>
        </MenuItem>
        {groupSubgroups.map((subgroup) => (
          <MenuItem key={subgroup.id} value={subgroup.id}>
            <Typography noWrap>{subgroup.name}</Typography>
          </MenuItem>
        ))}
        {groupSubgroups.length === 0 && (
          <MenuItem disabled>
            <Typography>no Subgroup yet</Typography>
          </MenuItem>
        )}
        {canCreateSubgroup && [
          <Divider key="new-subgroup-divider" />,
          <MenuItem key="new-subgroup" sx={{ gap: 1 }} onClick={() => setShowNewSubgroup(true)}>
            <AddRoundedIcon color="primary" />
            <Typography color="primary">New Sub-Group</Typography>
          </MenuItem>,
        ]}
      </TextField>
      {showNewSubgroup && group && (
        <NewSubgroupDialog
          group={group}
          existingSubgroups={groupSubgroups}
          onCreated={handleCreated}
          onClose={() => setShowNewSubgroup(false)}
        />
      )}
    </>
  );
};

export default SubgroupSelect;
